import React, { memo } from 'react';
import { ClockIcon, CheckCircleIcon, StarIcon } from 'lucide-react';
export const Advertorial = () => {
  const handleCtaClick = () => {
    window.location.href = 'https://luvani.co';
  };
  return <div className="flex flex-col w-full min-h-screen bg-white font-sans" data-id="element-2">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6" data-id="element-3">
        {/* Headline */}
        <h1 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-4">
          How This Mother of Three Erased Her Stretch Marks in Just 2 Weeks
          Using NASA Technology
        </h1>
        {/* Date and Disclaimer */}
        <div className="flex items-center text-gray-600 mb-4 text-sm" data-id="element-5">
          <ClockIcon className="h-5 w-5 mr-1" data-id="element-6" />
          <span data-id="element-7">January 28, 2025</span>
        </div>
        <p className="text-sm italic text-gray-500 mb-6" data-id="element-8">
          *Sarah M. is a real customer and was compensated for her endorsement.
          Results may vary based on individual circumstances, consistency of
          use, and other factors.
        </p>
        {/* Main Story */}
        <div className="mb-8" data-id="element-9">
          <div className="flex flex-col md:flex-row gap-6 mb-8" data-id="element-10">
            <div className="md:w-1/2" data-id="element-11">
              <p className="mb-4 text-lg" data-id="element-12">
                Sarah M., 34, never imagined that the beautiful stretch marks
                she earned bringing three precious lives into the world would
                become the source of such deep shame that she'd avoid intimacy
                with her husband for over two years.
              </p>
              <p className="mb-4 text-lg" data-id="element-13">
                As a devoted mother, Sarah treasured every moment with her
                children—reading bedtime stories, weekend park adventures, and
                family movie nights. Her body had done something miraculous, and
                she knew that. But when she looked in the mirror, all she could
                see were the deep, purple lines that seemed to mock her every
                attempt to feel beautiful again.
              </p>
              <p className="mb-4 text-lg" data-id="element-14">
                "I used to love getting dressed up for date nights with my
                husband," Sarah recalls, her voice trembling slightly. "But
                after my third pregnancy, I couldn't even look at myself without
                feeling devastated."
              </p>
            </div>
            <div className="md:w-1/2" data-id="element-15">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/t3PWeJYUHP4pvYuU2S9J7X/Screenshot_2025-07-20_at_2.14.46_PM.png" alt="Woman using Red Light Therapy Belt" className="w-full h-auto rounded-lg shadow-md" data-id="element-16" />
            </div>
          </div>
          <hr className="my-8 border-t border-gray-200" data-id="element-17" />
          <h2 className="text-2xl font-bold mb-4" data-id="element-18">
            Sarah's story began like so many others—with the joy of pregnancy
            quickly overshadowed by an unexpected reality check.
          </h2>
          <p className="mb-4 text-lg" data-id="element-19">
            During her third pregnancy, Sarah diligently applied every cream,
            oil, and lotion the internet promised would prevent stretch marks.
            She religiously rubbed cocoa butter and bio-oil on her growing belly
            twice daily, drank collagen supplements, and even invested in
            expensive "stretch mark prevention" serums that cost over $80 per
            bottle.
          </p>
          <p className="mb-4 text-lg" data-id="element-20">
            "I did everything right," Sarah says, cringing at the memory. "My
            doctor even said I had 'good genetics' and probably wouldn't get
            them. Then, at 37 weeks, I woke up and my entire belly was covered
            in these angry, deep purple marks. It was like my skin had betrayed
            me overnight."
          </p>
          <div className="bg-gray-100 p-6 rounded-lg my-8" data-id="element-21">
            <p className="text-lg font-semibold mb-2" data-id="element-22">
              Sarah is not alone. According to medical research, up to 90% of
              women develop stretch marks during pregnancy, and for many, these
              marks become a source of lasting emotional distress.
            </p>
          </div>
          <p className="mb-4 text-lg" data-id="element-23">
            In fact, a recent study published in the American Journal of
            Dermatology found that traditional stretch mark treatments show
            minimal to no improvement in 85% of cases, leaving millions of women
            feeling hopeless and financially drained.
          </p>
          <div className="flex flex-col md:flex-row gap-6 my-8 items-center" data-id="element-24">
            <div className="md:w-1/2" data-id="element-25">
              <h3 className="text-xl font-bold mb-3" data-id="element-26">The Discovery</h3>
              <p className="mb-4 text-lg" data-id="element-27">
                Emma showed Sarah a small, sleek device that looked nothing like
                the bulky machines she'd seen at dermatology clinics.
              </p>
              <p className="mb-4 text-lg" data-id="element-28">
                "This is what completely transformed my stretch marks," Emma
                said, holding up what looked like a simple belt with small
                lights embedded in it. "It's called a red light therapy belt. I
                know it sounds too good to be true, but I swear by it."
              </p>
            </div>
            <div className="md:w-1/2" data-id="element-29">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3dEJc4XjWtMPVdUoS7QAAD/adv_image_mid.png" alt="Woman using Red Light Therapy Belt on bed" className="w-full h-auto rounded-lg shadow-md" data-id="element-30" />
            </div>
          </div>
        </div>
        {/* Technology Section */}
        <div className="bg-gray-100 p-6 rounded-lg my-8" data-id="element-31">
          <h2 className="text-2xl font-bold mb-4 text-center" data-id="element-32">
            Red Light Therapy Uses NASA-Developed Technology to Trigger Deep
            Cellular Healing That Creams and Lotions Simply Cannot Reach
          </h2>
          <p className="mb-4 text-sm" data-id="element-33">
            The red light therapy belt works through a breakthrough process
            called photobiomodulation—the same technology NASA originally
            developed to help astronauts heal faster in space.
          </p>
          <div className="my-6" data-id="element-34">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ipGahD36Q6JdhVEhvG6oFz/explanation.jpg" alt="Red light therapy penetration diagram showing how different wavelengths reach skin layers" className="w-full h-auto rounded-lg shadow-md mb-4" data-id="element-35" />
            <p className="text-sm text-center italic mb-4" data-id="element-36">
              The science behind red light therapy: 660nm wavelengths penetrate
              the dermis, while 850nm wavelengths reach deep into the
              hypodermis, promoting healing and recovery at multiple skin
              levels.
            </p>
          </div>
          <p className="mb-4 text-sm" data-id="element-37">
            It works by delivering specific wavelengths of red and near-infrared
            light (660nm and 850nm) that penetrate deep into the skin layers
            where stretch marks actually form. Unlike surface treatments that
            only moisturize the top layer of skin, red light therapy stimulates
            cellular regeneration at the source of the problem.
          </p>
          <div className="my-6" data-id="element-38">
            <h3 className="font-bold mb-2 text-base" data-id="element-39">
              When these specific light wavelengths reach your skin cells, they
              trigger three powerful healing processes:
            </h3>
            <ul className="list-none space-y-2" data-id="element-40">
              <li className="flex items-start" data-id="element-41">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" data-id="element-42" />
                <span className="text-sm" data-id="element-43">
                  <strong data-id="element-44">Increased Collagen Production</strong> - The light
                  stimulates fibroblasts to produce new collagen, which helps
                  fill in and smooth stretch marks from within
                </span>
              </li>
              <li className="flex items-start" data-id="element-45">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" data-id="element-46" />
                <span className="text-sm" data-id="element-47">
                  <strong data-id="element-48">Enhanced Circulation</strong> - Improved blood flow
                  brings healing nutrients to damaged skin tissue
                </span>
              </li>
              <li className="flex items-start" data-id="element-49">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" data-id="element-50" />
                <span className="text-sm" data-id="element-51">
                  <strong data-id="element-52">Cellular Repair</strong> - The light energy helps
                  damaged cells repair themselves and regenerate healthy tissue
                </span>
              </li>
            </ul>
          </div>
        </div>
        {/* Results Section */}
        <div className="mb-8" data-id="element-53">
          <h2 className="text-2xl font-bold mb-4" data-id="element-54">
            Real Results from a Red Light Therapy Belt Customer: How NASA
            Technology Transformed Her Skin and Confidence in Just Weeks
          </h2>
          <div className="flex flex-col md:flex-row gap-6 my-8 items-center" data-id="element-55">
            <div className="md:w-1/2" data-id="element-56">
              <p className="mb-4 text-lg" data-id="element-57">
                Within just three weeks of using the red light therapy belt,
                Sarah experienced a transformation that went far beyond her
                skin.
              </p>
              <p className="mb-4 text-lg" data-id="element-58">
                Not only did her stretch marks fade dramatically, but she also
                noticed her skin becoming firmer and more elastic overall. The
                deep purple marks that had once been so prominent were now
                barely visible, and her confidence was returning for the first
                time in years.
              </p>
              <p className="mb-4 text-lg" data-id="element-59">
                "I couldn't believe the change," Sarah says. "It wasn't just
                that my stretch marks were fading—my entire belly looked
                smoother and tighter. I actually started wearing fitted shirts
                again."
              </p>
            </div>
            <div className="md:w-1/2" data-id="element-60">
              <div className="grid grid-cols-2 gap-8 max-w-[800px] mx-auto" data-id="element-61">
                <div className="flex flex-col" data-id="element-62">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qPq1ZsrmSGTBHQDBa5TuRm/ba_2.1.jpg" alt="Before using Red Light Therapy Belt - visible stretch marks" className="w-full h-auto rounded-lg shadow-md object-cover aspect-square" data-id="element-63" />
                  <p className="text-center font-bold mt-3 text-lg" data-id="element-64">BEFORE</p>
                </div>
                <div className="flex flex-col" data-id="element-65">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rrkjCbhhes9B3f5KgzkDbi/ba_2.2.jpg" alt="After using Red Light Therapy Belt - reduced stretch marks" className="w-full h-auto rounded-lg shadow-md object-cover aspect-square" data-id="element-66" />
                  <p className="text-center font-bold mt-3 text-lg" data-id="element-67">AFTER</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Testimonial Section */}
        <div className="border border-gray-200 rounded-lg p-6 mb-8" data-id="element-68">
          <h2 className="text-2xl font-bold mb-4 text-center" data-id="element-69">
            "My Honest Review and Results of Red Light Therapy"
          </h2>
          <p className="italic mb-6 text-center text-lg" data-id="element-70">
            "I'm usually skeptical of 'miracle' devices, but as a working mom, I
            needed something that actually worked without taking time away from
            my family."
          </p>
          <div className="space-y-6" data-id="element-71">
            <div data-id="element-72">
              <h3 className="font-bold mb-2 text-base" data-id="element-73">Week 1</h3>
              <p className="text-sm" data-id="element-74">
                When I first heard about red light therapy belts, I was
                doubtful. Another device promising to fix stretch marks? I'd
                been burned by expensive creams and treatments before. But the
                NASA connection intrigued me, and the 90-day guarantee meant I
                had nothing to lose. The setup was incredibly simple—just wrap
                it around my midsection and press start. After a week, I noticed
                my skin felt softer, but I tried not to get my hopes up.
              </p>
            </div>
            <div data-id="element-75">
              <h3 className="font-bold mb-2 text-base" data-id="element-76">Week 2</h3>
              <p className="text-sm" data-id="element-77">
                By the second week, the changes were becoming obvious. The angry
                purple color of my stretch marks was definitely fading, and my
                skin looked smoother overall. My husband even commented that I
                seemed more confident. The convenience factor was amazing—I
                could use it while cooking dinner or helping with homework.
              </p>
            </div>
            <div data-id="element-78">
              <h3 className="font-bold mb-2 text-base" data-id="element-79">Week 3</h3>
              <p className="text-sm" data-id="element-80">
                This is when I became a true believer. My stretch marks had
                faded so much that I actually wore a fitted dress to a work
                event—something I hadn't done in three years. Friends started
                asking if I'd lost weight or done something different. The
                transformation wasn't just physical; I felt like myself again.
              </p>
            </div>
            <div data-id="element-81">
              <h3 className="font-bold mb-2 text-base" data-id="element-82">The Bottom Line</h3>
              <p className="text-sm" data-id="element-83">
                After trying countless products and treatments, this is the
                first thing that actually delivered results. The science makes
                sense, the convenience fits my lifestyle, and most importantly,
                it works. I've recommended it to every mom I know, and they're
                all seeing similar results.
              </p>
            </div>
          </div>
          <p className="text-sm italic text-gray-500 mt-4 text-center" data-id="element-84">
            Customer results may vary based on individual circumstances and
            consistency of use.
          </p>
        </div>
        {/* Pricing Section */}
        <div className="mb-8" data-id="element-85">
          <h2 className="text-2xl font-bold mb-4" data-id="element-86">
            I Expected This Technology to Cost Thousands—You Won't Believe How
            Affordable It Actually Is
          </h2>
          <p className="mb-4 text-lg" data-id="element-87">
            After researching clinical red light therapy treatments that cost
            $200-500 per session, I was shocked to discover that this NASA-grade
            technology was available for home use at a fraction of the cost.
          </p>
          <p className="mb-4 text-lg" data-id="element-88">
            Professional dermatology clinics charge thousands for red light
            therapy treatments, and the results require ongoing maintenance
            sessions. I initially thought there had to be a catch with the
            affordable pricing.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4" data-id="element-89">
            <p className="font-bold text-lg" data-id="element-90">
              Given how quickly these devices have been selling out due to their
              growing popularity among mothers and wellness enthusiasts, I
              wouldn't be surprised if prices increase soon.
            </p>
          </div>
          <p className="mb-4 text-lg" data-id="element-91">
            Right now, there's a limited-time promotion offering significant
            savings on multi-device bundles, allowing families to treat multiple
            areas or share devices. This is a rare opportunity to access
            professional-grade light therapy at an unprecedented value.
          </p>
        </div>
        {/* Update Section */}
        <div className="bg-gray-100 p-6 rounded-lg mb-8" data-id="element-92">
          <h3 className="font-bold mb-2 text-base" data-id="element-93">UPDATE: January 28, 2025</h3>
          <p className="text-sm" data-id="element-94">
            Since this story was originally published, there's been tremendous
            interest in red light therapy belts, and several manufacturers have
            seen huge surges in sales. While inventory is now limited, they're
            still offering an "Internet Exclusive" promotion with bundle
            discounts. (This offer is only available while supplies last.) To
            find out if red light therapy belts are still in stock, click the
            button below.
          </p>
        </div>
        {/* CTA Button - Keeping only the limited stock offer button */}
        <div className="flex flex-col gap-4 mb-8" data-id="element-95">
          <button className="bg-sky-500 hover:bg-sky-600 text-white font-bold py-4 px-6 rounded-lg text-xl transition duration-200" onClick={handleCtaClick} data-id="element-96">
            START YOUR HEALING JOURNEY TODAY
          </button>
        </div>
        <p className="text-center font-bold text-lg mb-8" data-id="element-97">
          Limited Inventory Available
        </p>
      </div>
    </div>;
};